import { useQueryClient, UseQueryOptions } from 'react-query'
import { createGroup, deleteGroup, getGroups, updateGroup, getGroupMembers } from 'features/groups/api/groups'
import { Group, GroupDeletePayload, GroupMember, GroupUpdatePayload } from '../types'

const groupsKeys = {
  all: ['groups'] as const,
  members: (groupId: string) => ['groupMembers', groupId] as const
}

export const groupMembersQuery = (groupId: string): UseQueryOptions<GroupMember[] | undefined, Error> => ({
  queryKey: groupsKeys.members(groupId),
  queryFn: () => getGroupMembers(groupId),
  staleTime: Infinity
})

function useGroupsQueries() {
  const queryClient = useQueryClient()

  const queries = {
    getGroups: {
      queryKey: groupsKeys.all,
      queryFn: getGroups,
      staleTime: Infinity
    },
    getGroupMembers: groupMembersQuery,
    createGroup: {
      mutationFn: createGroup,
      onSuccess: (data: Group) => {
        queryClient.setQueryData<Group[]>(groupsKeys.all, (groups) => [data, ...(groups || [])])
      }
    },
    updateGroup: {
      mutationFn: (data: GroupUpdatePayload) => updateGroup(data.groupId, data.payload),
      onSuccess: (data: Group) => {
        queryClient.setQueryData<Group[] | undefined>(groupsKeys.all, (groups: Group[] | undefined) =>
          (groups || []).map((group) => {
            if (group.id === data.id) {
              return {
                ...group,
                ...data
              }
            }

            return group
          })
        )
      }
    },
    deleteGroup: {
      mutationFn: (data: GroupDeletePayload) => deleteGroup(data.groupId),
      onSuccess: (_, variables: GroupDeletePayload) => {
        queryClient.setQueryData<Group[] | undefined>(groupsKeys.all, (groups: Group[] | undefined) =>
          (groups || []).filter((group) => group.id !== variables.groupId)
        )
      }
    }
  }

  return { queries }
}

export default useGroupsQueries
